import type { MessageDescriptor } from '@lingui/core'
import { useLingui } from '@lingui/react/macro'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'designSystem/components/Tooltip'
import { CircleHelp } from 'lucide-react'
import { FC, ReactNode } from 'react'
import styled from 'styled-components'

const LabelRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  max-width: 100%;
`

const HelpBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.forecast.secondaryText};
  cursor: help;
  opacity: 0.75;
  flex-shrink: 0;
  line-height: 0;

  &:hover {
    opacity: 1;
    color: ${({ theme }) => theme.forecast.defaultText};
  }
`

const TipBody = styled.div`
  max-width: 16.5rem;
  text-align: left;
  line-height: 1.35;
  font-weight: 400;
  white-space: normal;
`

export interface KnobHelpProps {
  /** Visible label text (or custom node). */
  label: ReactNode
  /** Hover explanation. */
  help: string | MessageDescriptor
  /** Optional: wrap as a block field label. */
  block?: boolean
}

/** Label + small “?” that shows a design-system tooltip on hover. */
export const KnobHelp: FC<KnobHelpProps> = ({ label, help, block }) => {
  const { i18n, t } = useLingui()

  const content = (
    <LabelRow>
      <span>{label}</span>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <HelpBtn
            type='button'
            aria-label={
              typeof label === 'string'
                ? t`About ${label}`
                : t`About this setting`
            }
            onClick={(e) => e.preventDefault()}>
            <CircleHelp size={12} strokeWidth={2} />
          </HelpBtn>
        </TooltipTrigger>
        <TooltipContent side='right' align='start' sideOffset={6}>
          <TipBody>{typeof help === 'string' ? help : i18n._(help)}</TipBody>
        </TooltipContent>
      </Tooltip>
    </LabelRow>
  )
  if (block) {
    return <div style={{ display: 'block' }}>{content}</div>
  }
  return content
}
